import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { fileApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import type { Task } from "~/types/api";

type RenameTaskParams = {
  taskId: number;
  name: string;
};

export const useTaskMutations = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const renameTask = useMutation({
    mutationFn: async ({ taskId, name }: RenameTaskParams) => {
      const response = await fileApi.client.PUT("/api/Task/{taskId}/rename", {
        params: { path: { taskId } },
        body: { name },
      });

      if (response.error) {
        throw new Error(getErrorMessage(response.error, "Failed to rename task"));
      }
      return response.data;
    },
    onMutate: async ({ taskId, name }) => {
      const queryKey = fileApi.queryOptions("get", "/api/Task").queryKey;
      await queryClient.cancelQueries({ queryKey });

      const previousTasks = queryClient.getQueryData<Task[]>(queryKey);

      if (previousTasks) {
        queryClient.setQueryData<Task[]>(queryKey, (old) => {
          if (!old) return [];
          return old.map((t) => (t.id === taskId ? { ...t, name } : t));
        });
      }

      return { previousTasks };
    },
    onError: (err, vars, context) => {
      const queryKey = fileApi.queryOptions("get", "/api/Task").queryKey;
      if (context?.previousTasks) {
        queryClient.setQueryData(queryKey, context.previousTasks);
      }
      showToast("error", err.message, "Failed to rename task");
    },
    onSuccess: () => {
      const queryKey = fileApi.queryOptions("get", "/api/Task").queryKey;
      queryClient.invalidateQueries({ queryKey });
      showToast("success", "Task renamed");
    },
  });

  return { renameTask };
};
